import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../../providers/AuthProvider';
import { IngredientForm } from '../ingredients/IngredientForm';
import { ErrorMessage } from '../ErrorMessage';
import { validateIngredientForm } from '../../utils/ingredientValidation';
import { createIngredient } from '../../graphql/ingredients';
import type { IngredientFormData, IngredientValidationError } from '../../types/ingredient';

const client = generateClient();

interface ImportReviewScreenProps {
  formData: IngredientFormData;
  autoPopulatedFields: Set<keyof IngredientFormData>;
  onCancel: () => void;
  onSaved: (ingredientId: string) => void;
}

export function ImportReviewScreen({
  formData: initialFormData,
  autoPopulatedFields: initialAutoPopulatedFields,
  onCancel,
  onSaved,
}: ImportReviewScreenProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<IngredientFormData>(initialFormData);
  const [autoPopulated, setAutoPopulated] = useState<Set<keyof IngredientFormData>>(
    () => new Set(initialAutoPopulatedFields)
  );
  const [validationErrors, setValidationErrors] = useState<IngredientValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isNameEmpty = formData.name.trim().length === 0;

  const handleFieldChange = useCallback(
    (field: keyof IngredientFormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Remove auto-populated indicator when user edits the field
      setAutoPopulated((prev) => {
        if (prev.has(field)) {
          const next = new Set(prev);
          next.delete(field);
          return next;
        }
        return prev;
      });
      // Clear validation error for this field
      setValidationErrors((prev) => prev.filter((e) => e.field !== field));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      const errors = validateIngredientForm(formData);
      setValidationErrors(errors);

      if (errors.length > 0) {
        return;
      }

      const parseCannabinoid = (val: string): number | undefined => {
        if (val === '') return undefined;
        return parseFloat(val);
      };

      setIsSubmitting(true);

      try {
        const newIngredientId = crypto.randomUUID();
        const now = new Date().toISOString();

        await client.graphql({
          query: createIngredient,
          variables: {
            input: {
              userId: user!.userId,
              ingredientId: newIngredientId,
              name: formData.name.trim(),
              brand: formData.brand || undefined,
              strain: formData.strain || undefined,
              varietal: formData.varietal || undefined,
              thc: parseCannabinoid(formData.thc),
              thca: parseCannabinoid(formData.thca),
              cbd: parseCannabinoid(formData.cbd),
              cbda: parseCannabinoid(formData.cbda),
              cbg: parseCannabinoid(formData.cbg),
              cbn: parseCannabinoid(formData.cbn),
              createdAt: now,
              updatedAt: now,
            },
          },
        });

        onSaved(newIngredientId);
      } catch {
        setSubmitError('Failed to save ingredient. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, user, onSaved]
  );

  return (
    <div className="import-review-screen">
      <div className="import-review-header">
        <h3>Review Imported Data</h3>
        <p className="import-review-description">
          Review and edit the data imported from the product label. Fields marked with a COA badge
          were automatically populated from the certificate of analysis.
        </p>
      </div>

      {submitError && <ErrorMessage message={submitError} />}

      <div className="import-review-form">
        <IngredientForm
          formData={formData}
          validationErrors={validationErrors}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          isEditMode={false}
          autoPopulatedFields={autoPopulated}
          showRequiredIndicator={isNameEmpty}
          submitLabel="Save Ingredient"
          submitDisabled={isNameEmpty}
        />
      </div>

      <div className="import-review-actions">
        <button
          type="button"
          className="cta-button-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
